import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import CachedIcon from '@material-ui/icons/Cached';
import Select, { components } from "react-select";
import SearchIcon from "mdi-react/SearchIcon";
import { setAuthProgram } from "@/containers/App/auth";
import { getProgram } from "@/service/program/getProgram";
import { getProgramBalance } from "@/service/program/getBalance";
import { getProgramTree } from "@/service/program/getProgramTree";
import { cacheProgramTree, getCachedProgramTree } from "@/shared/helpers";


const SelectProgram = ({
  auth,
  program,
  rootProgram,
  showRefresh,
  onChange,
  selected = null,
  label = 'Program',
  variant = 'default',
}) => {
  const history = require('react-router-dom').useHistory();
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const isManagerReportVariant = variant === "manager-report";
  const controlHeight = isManagerReportVariant ? "1.9rem" : "2rem";
  const controlRadius = isManagerReportVariant ? "12px" : "10px";
  const controlFontSize = isManagerReportVariant ? "0.8rem" : "0.8125rem";
  const optionFontSize = isManagerReportVariant ? "0.8rem" : "0.875rem";
  const optionChildFontSize = isManagerReportVariant ? "0.76rem" : "0.8125rem";

   useEffect(() => {
     const cachedTree = getCachedProgramTree();
     if (cachedTree) {
       const flatOptions = flattenPrograms(cachedTree);
       setOptions(flatOptions);
       setDefaultSelected(flatOptions);
     } else {
       if (rootProgram && rootProgram.id) {
         getProgramTree(rootProgram.organization_id, rootProgram.id).then(
           (tree) => {
             cacheProgramTree(tree);
             const flatOptions = flattenPrograms(tree);
             setOptions(flatOptions);
             setDefaultSelected(flatOptions);
           }
         );
       }
     }
   }, [rootProgram]);

   const flattenPrograms = (programs, depth = 0) => {
     let list = [];
     programs.forEach((p) => {
       list.push({
         value: p.id,
         label: p.name,
         depth: depth,
         isDisabled: p.type == 'shell'
       });
       if (p.children && p.children.length) {
         list = list.concat(flattenPrograms(p.children, depth + 1));
       }
     });
     return list;
   };

  const setDefaultSelected = (optionsList) => {
    const selectedValue = selected || program?.id;
    const found = optionsList.find((opt) => opt.value === selectedValue);
    if (found) setSelectedOption(found);
  };

   const handleChange = (selected) => {
     setSelectedOption(selected);
     if (typeof onChange === "function") {
       onChange({ target: { value: selected.value } }); // mimic event
       return;
     }

     history.push(`/program/${selected.value}/csv-import`);
   };


 const refreshProgramTree = () => {
   getProgramTree(rootProgram.organization_id, rootProgram.id).then((tree) => {
     cacheProgramTree(tree);
     const flatOptions = flattenPrograms(tree);
     setOptions(flatOptions);
     setDefaultSelected(flatOptions);
   });
 };

  if (!auth || !program) return "loading";

  return (
    <>
      <div className="form__form-group-field d-flex" style={{ alignItems: "center", width: "100%" }}>
        {label && (
          <span
            className="form__form-group-label"
            style={{
              fontSize: "0.8125rem",
              marginRight: "0.5rem",
              whiteSpace: "nowrap",
              flex: "0 0 auto",
            }}
          >
            {label}:
          </span>
        )}
         <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <Select
            value={selectedOption}
            onChange={handleChange}
            options={options}
            isSearchable
            placeholder={"Search programs..."}
            className="react-select"
            classNamePrefix="react-select"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            formatOptionLabel={(option, { context }) => {
              if (context === "value") return option.label;
              const depth = option.depth || 0;
              return (
                <div style={{
                  paddingLeft: `${depth * 20}px`,
                  color: depth > 0 ? "#6b7280" : "#111827",
                  fontSize: depth > 0 ? optionChildFontSize : optionFontSize,
                }}>
                  {option.label}
                </div>
              );
            }}
            components={{
              DropdownIndicator: (props) => (
                <components.DropdownIndicator {...props}>
                  <SearchIcon size={18} />
                </components.DropdownIndicator>
              ),
            }}
            styles={{
              container: (base) => ({
                ...base,
                flex: "1 1 auto",
                minWidth: "0",
                maxWidth: "100%",
                width: "100%",
              }),
              control: (base) => ({
                ...base,
                borderRadius: controlRadius,
                cursor: "text",
                minHeight: controlHeight,
                height: controlHeight,
                fontSize: controlFontSize,
                borderColor: isManagerReportVariant ? "rgba(148, 163, 184, 0.22)" : base.borderColor,
                boxShadow: "none",
              }),
              valueContainer: (base) => ({
                ...base,
                height: controlHeight,
                padding: "0 0.5rem",
              }),
              input: (base) => ({
                ...base,
                margin: 0,
                padding: 0,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: controlHeight,
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 2200,
                ...(isManagerReportVariant
                  ? {}
                  : {
                      minWidth: "18rem",
                      maxWidth: "min(28rem, calc(100vw - 32px))",
                    }),
              }),
              menu: (base) => ({
                ...base,
                textAlign: "left",
                left: 0,
                right: "auto",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                zIndex: 2200,
                ...(isManagerReportVariant
                  ? {}
                  : {
                      minWidth: "100%",
                    }),
              }),
              option: (base, state) => ({
                ...base,
                textAlign: "left",
                padding: "0.5rem 0.75rem",
                fontWeight: state.isSelected ? "bold" : "normal",
                backgroundColor: state.isSelected
                  ? "#e6f0ff"
                  : state.isFocused
                  ? "#f5f5f5"
                  : "white",
                color: "#333",
                fontSize: controlFontSize,
              }),
              indicatorSeparator: () => ({
                display: "none",
              }),
              singleValue: (base) => ({
                ...base,
                textAlign: "left",
                fontSize: controlFontSize,
              }),
            }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 12,
              colors: {
                ...theme.colors,
                primary: "#F97316",
                primary25: "rgba(249, 115, 22, 0.08)",
                primary50: "rgba(249, 115, 22, 0.14)",
              },
            })}
          />
        </div>
      </div>
      {showRefresh && <span alt="Refresh Program Tree"><CachedIcon style={{ cursor: "pointer" }} onClick={refreshProgramTree} /></span>}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    auth: state.auth,
    program: ownProps.rootProgram || state.program,
    rootProgram: ownProps.rootProgram || state.rootProgram,
  };
};

export default connect(mapStateToProps)(SelectProgram);
